import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Eye, EyeOff, Star } from "lucide-react";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { PageHead } from "@/components/admin/PageHead";
import StatusBadge from "@/components/common/StatusBadge";
import ConfirmDelete from "@/components/admin/ConfirmDelete";
import TestimonialFormDialog from "@/components/admin/TestimonialFormDialog";

const FILTERS = ["all", "published", "pending", "hidden"];

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(undefined);
  const load = useCallback(() => api.get("/admin/testimonials").then(({ data }) => setItems(data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (err) { toast.error(formatApiError(err)); }
  };
  const rows = items.filter((t) => filter === "all" || t.status === filter);

  return (
    <div data-testid="admin-testimonials-page">
      <PageHead title="Testimonials" subtitle="Published testimonials scroll on the home, stocks and properties pages. Client submissions arrive as pending.">
        <button onClick={() => setEditing(null)} className="btn-primary" data-testid="admin-add-testimonial-button"><Plus className="h-4 w-4" /> Add testimonial</button>
      </PageHead>
      <div className="mb-6 inline-flex rounded-full border border-line p-1">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`admin-testimonials-filter-${f}`}
            className={`rounded-full px-4 py-2.5 text-xs capitalize sm:py-1.5 transition-colors duration-200 ${filter === f ? "bg-white/10 text-white" : "text-slate-400 hover:text-white"}`}>{f}</button>
        ))}
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {rows.map((t) => (
          <div key={t.id} className="panel flex flex-col p-6" data-testid={`admin-testimonial-${t.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={t.status} />
              <span className={`rounded-full border px-2.5 py-0.5 font-mono text-[11px] uppercase tracking-wider ${t.category === "stocks" ? "border-emerald-500/30 text-emerald-400" : "border-amber-600/40 text-amber-500"}`}>{t.category === "stocks" ? "Stocks" : "Real estate"}</span>
              <span className="font-mono text-[11px] uppercase tracking-wider text-slate-500">{t.source === "client" ? "Client submitted" : "Added by admin"}</span>
              <span className="ml-auto flex">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className={`h-3.5 w-3.5 ${i <= t.rating ? "fill-amber-500 text-amber-500" : "text-slate-700"}`} />)}</span>
            </div>
            <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">“{t.quote}”</p>
            <div className="mt-5 flex items-center justify-between border-t border-line pt-4">
              <p className="text-sm"><span className="text-white">{t.name}</span>{t.location && <span className="text-slate-500"> · {t.location}</span>}<span className="block text-xs text-slate-500">{fmtDate(t.created_at)}</span></p>
              <div className="flex items-center">
                <button onClick={() => run(() => api.patch(`/admin/testimonials/${t.id}/status`, { status: t.status === "published" ? "hidden" : "published" }), t.status === "published" ? "Hidden from site" : "Published")}
                  className="inline-flex items-center gap-1.5 rounded-full border border-line px-3 py-1 text-xs text-slate-300 hover:border-emerald-500 hover:text-emerald-400" data-testid={`admin-testimonial-toggle-${t.id}`}>
                  {t.status === "published" ? <><EyeOff className="h-3.5 w-3.5" /> Hide</> : <><Eye className="h-3.5 w-3.5" /> Publish</>}
                </button>
                <button onClick={() => setEditing(t)} className="ml-1 rounded-lg p-2 text-slate-500 hover:bg-white/5 hover:text-white" data-testid={`admin-testimonial-edit-${t.id}`}><Pencil className="h-4 w-4" /></button>
                <ConfirmDelete label="this testimonial" description="It will be permanently removed from the site." onConfirm={() => run(() => api.delete(`/admin/testimonials/${t.id}`), "Testimonial deleted")} testId={`admin-testimonial-delete-${t.id}`} />
              </div>
            </div>
          </div>
        ))}
      </div>
      {!rows.length && <p className="text-slate-500" data-testid="admin-testimonials-empty">No testimonials here yet.</p>}
      <TestimonialFormDialog open={editing !== undefined} onOpenChange={(o) => !o && setEditing(undefined)} item={editing} onSaved={load} />
    </div>
  );
}
